/**
 * Engagement engine helpers — pure derivation + localStorage persistence.
 *
 * Mirrors the `appearance.ts` persistence pattern (hydrate-at-load / save).
 * Phase 1 derives prompts purely from the user's tournaments; no backend.
 */

import type { MyTournament } from '@/types';
import { TOURNAMENT_STATUS } from '@/types/enums';
import type {
  EngagementKind,
  EngagementPriority,
  EngagementPrompt,
  InboxItem,
} from '@/types/engagement';
import { storage, StorageKey } from './storage';

/** Max inbox items kept (oldest evicted). */
export const MAX_INBOX = 30;
/** A surfaced prompt won't re-nudge within this window (also a gentle reminder cadence). */
export const COOLDOWN_MS = 12 * 60 * 60 * 1000; // 12h
/** Nudge auto-dismiss delay (ms). */
export const NUDGE_AUTO_DISMISS_MS = 6500;
/** Nudge slide transition duration (ms) — keep in sync with the Tailwind class. */
export const NUDGE_SLIDE_MS = 300;

const PRIORITY_RANK: Record<EngagementPriority, number> = { urgent: 0, high: 1, normal: 2 };

/**
 * Derive engagement prompts from the user's tournaments. Pure + deterministic:
 * the same condition yields the same `id`/`dedupeKey` across polls (enabling
 * dedup + cooldown). At most one prompt per tournament (phase-encoded id, so
 * register → checkin → play are three distinct prompts).
 *
 * NB: `MyTournament` carries no per-me check-in flag, so `checkin` is derived
 * from status alone; the cooldown shows it once and `pruneStale` retires it
 * when the status moves on.
 */
export function deriveTournamentPrompts(
  rows: MyTournament[],
  now: number,
): EngagementPrompt[] {
  const prompts: EngagementPrompt[] = [];

  for (const t of rows) {
    const common = {
      source: 'client' as const,
      createdAt: now,
      params: { tournament: t.title } as Record<string, string | number>,
      target: `/tournaments/${t.id}`,
    };

    if (t.status === TOURNAMENT_STATUS.REGISTRATION_OPENED && !t.is_joined) {
      const key = `t.register.${t.id}`;
      prompts.push({
        ...common,
        id: key,
        dedupeKey: key,
        kind: 'register',
        priority: 'normal',
        titleKey: 'engagement.register_title',
        bodyKey: 'engagement.register_body',
        recoverable: false,
      });
    } else if (t.status === TOURNAMENT_STATUS.CHECKIN && t.is_joined) {
      const key = `t.checkin.${t.id}`;
      prompts.push({
        ...common,
        id: key,
        dedupeKey: key,
        kind: 'checkin',
        priority: 'urgent',
        titleKey: 'engagement.checkin_title',
        bodyKey: 'engagement.checkin_body',
        recoverable: true,
      });
    } else if (
      (t.status === TOURNAMENT_STATUS.STARTED || t.status === TOURNAMENT_STATUS.IN_PROGRESS) &&
      t.is_joined
    ) {
      const key = `t.play.${t.id}`;
      prompts.push({
        ...common,
        id: key,
        dedupeKey: key,
        kind: 'play',
        priority: 'high',
        titleKey: 'engagement.play_title',
        bodyKey: 'engagement.play_body',
        recoverable: true,
      });
    }
  }

  // Most time-sensitive first.
  prompts.sort((a, b) => PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority]);
  return prompts;
}

export interface PersistedEngagement {
  inbox: InboxItem[];
  cooldowns: Record<string, number>;
}

/** Read persisted inbox + cooldowns (localStorage). Falls back to empty. */
export function loadStoredEngagement(): PersistedEngagement {
  const raw = storage.get(StorageKey.Engagement);
  if (!raw) return { inbox: [], cooldowns: {} };
  try {
    const parsed = JSON.parse(raw) as Partial<PersistedEngagement>;
    const inbox = Array.isArray(parsed.inbox) ? parsed.inbox.slice(0, MAX_INBOX) : [];
    const cooldowns =
      parsed.cooldowns && typeof parsed.cooldowns === 'object' ? parsed.cooldowns : {};
    return { inbox, cooldowns };
  } catch {
    return { inbox: [], cooldowns: {} };
  }
}

/** Persist inbox + cooldowns (localStorage). */
export function saveEngagement(data: PersistedEngagement): void {
  storage.set(
    StorageKey.Engagement,
    JSON.stringify({ inbox: data.inbox.slice(0, MAX_INBOX), cooldowns: data.cooldowns }),
  );
}

/** Fallback title for a prompt kind (used as the i18n `defaultValue`). */
export function defaultPromptTitle(kind: EngagementKind): string {
  switch (kind) {
    case 'register':
      return 'Iscriviti a {{tournament}}';
    case 'checkin':
      return 'Fai il check-in: {{tournament}}';
    case 'play':
      return 'Gioca il tuo match: {{tournament}}';
    default:
      return '{{tournament}}';
  }
}

/** Fallback secondary line for a prompt kind (used as the i18n `defaultValue`). */
export function defaultPromptBody(kind: EngagementKind): string {
  switch (kind) {
    case 'register':
      return 'Iscrizioni aperte';
    case 'checkin':
      return 'Conferma la tua presenza ora';
    case 'play':
      return 'Il tuo match è pronto';
    default:
      return '';
  }
}
